import { PropertyType } from '@devicepilot/manifest-types';
import jsep from 'jsep';

/**
 * Constants for types of operations
 * @export
 */
export const Operation = {
  COMPARISON: 'comparison',
  NO_RIGHT: 'no-right',
  FUNCTION: 'function',
  BOOLEAN: 'boolean',
  TIME: 'time',
};

/**
 * Configuration for all types of operations
 * @export
 */
/* eslint-disable object-curly-newline */
export const ops = {
  // Generic
  is: { text: 'is', value: '==', type: Operation.COMPARISON, applyTo: ['string', 'number'] },
  isNot: { text: 'isn\'t', value: '!=', type: Operation.COMPARISON, applyTo: ['string', 'number'] },
  hasNoValue: { text: 'has no value', value: '== null', type: Operation.NO_RIGHT, applyTo: ['string', 'number'] },
  hasAnyValue: { text: 'has any value', value: '!= null', type: Operation.NO_RIGHT, applyTo: ['string', 'number'] },
  separator: { text: '──────────', value: null, disabled: true, applyTo: ['string', 'number'] },

  // Numbers
  fewerThan: { text: 'is less than', value: '<', type: Operation.COMPARISON, applyTo: ['number'] },
  fewerThanOrEqualTo: { text: 'is less than or equal to', value: '<=', type: Operation.COMPARISON, applyTo: ['number'] },
  greaterThan: { text: 'is more than', value: '>', type: Operation.COMPARISON, applyTo: ['number'] },
  greatThanOrEqualTo: { text: 'is more than or equal to', value: '>=', type: Operation.COMPARISON, applyTo: ['number'] },

  // String
  contains: { text: 'contains', value: 'contains({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  doesNotContain: { text: 'does not contain', value: 'doesNotContain({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  startsWith: { text: 'starts with', value: 'startsWith({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  doesNotStartWith: { text: 'doesn\'t start with', value: 'doesNotStartWith({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  endsWith: { text: 'ends with', value: 'endsWith({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  doesNotEndWith: { text: 'doesn\'t end with', value: 'doesNotEndWith({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
  regex: { text: 'matches regex', value: 'regex({prop}, {valueStr})', type: Operation.FUNCTION, applyTo: ['string'] },
};

export const sxsOps = {
  increasedByMoreThan: { text: 'has increased by more than', value: '{prop} > {valueNum}', type: Operation.FUNCTION },
  decreasedByMoreThan: { text: 'has decreased by more than', value: '{prop} < -{valueNum}', type: Operation.FUNCTION },
  changedByMoreThan: { text: 'has changed by more than', value: 'abs({prop}) > {valueNum}', type: Operation.FUNCTION },
};

/* eslint-enable object-curly-newline */

export const timeOps = {
  moreThan: { text: 'more than', value: '{prop} < ago({valueNum})', type: Operation.FUNCTION },
  lessThan: { text: 'less than', value: '{prop} > ago({valueNum})', type: Operation.FUNCTION },
};

export const timeModifiers = [
  { label: 'minutes ago', singleLabel: 'minute ago', modifier: 60 },
  { label: 'hours ago', singleLabel: 'hour ago', modifier: 3600 },
  { label: 'days ago', singleLabel: 'day ago', modifier: 86400 },
  { label: 'weeks ago', singleLabel: 'week ago', modifier: 604800 },
];

/**
 * Fetch the full operation object from the value (e.g. == or >)
 *
 * @param {String} op
 */
export const getOperationObject = (op) => {
  const allOps = {
    ...ops,
    ...sxsOps,
    ...timeOps,
  };

  return Object.values(allOps)
    .find((operation) => operation.value === op);
};

/**
 * Convert the output of the visual editor into a string that can be used for
 * querying the API.
 *
 * @export
 * @param {Array} visualData An array of rows from the visual data editor
 * @returns String
 */
export function getStringVersion(visualData, operator) {
  if (!visualData || !Array.isArray(visualData)) {
    return '';
  }

  const joinOperator = operator === 'and' ? ' && ' : ' || ';

  const stringMap = visualData.map((row) => {
    const { left } = row;
    let { right } = row;
    const op = getOperationObject(row.op);

    if (!op) {
      return null;
    }

    let rowString = '';
    if (!left || !left.field || op.value === null) {
      return null;
    }

    rowString += `${left.field}`;

    // Deal with different types of operations
    if (op.type === Operation.COMPARISON || op.type === Operation.BOOLEAN) {
      if (right === null) {
        return null;
      }

      if (left.type === PropertyType.String) {
        right = `"${row.right}"`;
      }
      rowString += ` ${op.value} ${right}`;
    } else if (op.type === Operation.FUNCTION) {
      // Construct functions by swapping out the tokens
      rowString = op.value
        .replace('{prop}', left.field)
        .replace('{valueStr}', `"${row.right}"`)
        .replace('{valueNum}', row.right);
    } else if (op.type === Operation.NO_RIGHT) {
      rowString += ` ${op.value}`;
    }

    return rowString;
  }).filter((row) => row !== null);

  return stringMap.join(joinOperator);
}

const onlyValidRow = (row) => ((row.left === undefined || row.op === undefined) ? null : row);
export function nodeToRow(node, properties) {
  const row = {
    left: null,
    op: null,
    right: null,
  };

  // Little helper function to convert properties to their full object
  const getProperty = (name) => (properties.length
    && properties.filter((p) => p.name === name || p.field === name || p === name)[0])
    || name;

  if (node.type === 'BinaryExpression') {
    if (node.right.type === 'Literal') {
      row.left = getProperty(node.left.name || node.left.raw);
      row.op = node.operator;

      if (node.right.value === null && row.op === '==') {
        row.op = ops.hasNoValue.value;
      } else if (node.right.value === null) {
        row.op = ops.hasAnyValue.value;
      }
      row.right = node.right.value;

      return onlyValidRow(row);
    } if (node.right.type === 'CallExpression' && node.right.callee.name === 'ago') {
      row.left = getProperty(node.left.name || node.left.raw);
      row.op = node.operator === '>' ? timeOps.lessThan.value : timeOps.moreThan.value;
      row.right = node.right.arguments[0].value;
      return onlyValidRow(row);
    } if (node.right.type === 'UnaryExpression' && node.right.operator === '-') {
      row.left = getProperty(node.left.name || node.left.raw);
      row.op = node.operator;
      if (node.right.argument) {
        row.right = parseFloat(node.right.operator + node.right.argument.value);
      } else {
        row.right = 0;
      }

      return onlyValidRow(row);
    }
  } else if (node.type === 'CallExpression' && node.arguments.length === 2) {
    Object.keys(ops).forEach((key) => {
      if (key === node.callee.name) {
        row.left = getProperty(node.arguments[0].name || node.arguments[0].raw);
        row.op = ops[key].value;
        row.right = node.arguments[1].value;
      }
    });

    // Catch the case of a function that is not supported
    if (!row.left) {
      return null;
    }

    return onlyValidRow(row);
  }

  return null;
}

function isLogicalExpression(node) {
  return node.type === 'BinaryExpression' && (node.operator === '&&' || node.operator === '||');
}

export function getVisualVersion(query, properties) {
  let rootNode = null;
  try {
    rootNode = jsep(query);
  } catch (error) {
    return { error };
  }

  let nextNode = rootNode;
  const nodeArray = [];

  let operator = 'and';
  if (query.indexOf('||') > -1) {
    operator = 'or';
  }

  const error = 'Visual editor is not available for complex queries.';

  if (isLogicalExpression(rootNode)) {
    const { operator: rootOperation } = rootNode;
    // Grab all nested expressions
    while (isLogicalExpression(nextNode)) {
      if (nextNode.operator !== rootOperation) {
        return { error };
      }
      nodeArray.push(nextNode.right);
      nextNode = nextNode.left;
    }
    nodeArray.push(nextNode);
    nodeArray.reverse();
  } else {
    nodeArray.push(rootNode);
  }

  const rows = nodeArray.map((n) => nodeToRow(n, properties));

  return rows.some((r) => r === null)
    ? { error }
    : { rows, operator };
}

export function getVisualTime(timeInSeconds) {
  let displayedTime = timeInSeconds;
  let timeModifier = timeModifiers[0].modifier;
  let modifierLabel = timeModifiers[0].label;

  for (let i = timeModifiers.length - 1; i >= 0; i -= 1) {
    const m = timeModifiers[i];
    const modified = timeInSeconds / m.modifier;
    if (modified >= 1 && modified % 1 === 0) {
      displayedTime = timeInSeconds / m.modifier;
      timeModifier = m.modifier;

      if (displayedTime === 1) {
        modifierLabel = m.singleLabel;
      } else {
        modifierLabel = m.label;
      }
      break;
    }
  }

  return { displayedTime, timeModifier, modifierLabel };
}
